import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';
import type { Event } from '../types/event';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

interface NearbyEventsParams {
  lat: number;
  lng: number;
  radius_meters: number;
}

export async function fetchNearbyEvents({ lat, lng, radius_meters }: NearbyEventsParams): Promise<Event[]> {
  if (!Number.isFinite(lat) || lat < -90 || lat > 90) throw new Error('Invalid latitude');
  if (!Number.isFinite(lng) || lng < -180 || lng > 180) throw new Error('Invalid longitude');
  if (!Number.isFinite(radius_meters) || radius_meters <= 0) throw new Error('Invalid radius');

  try {
    const { data, error } = await supabase.rpc('nearby_events', {
      center_lat: lat,
      center_lon: lng,
      radius_m: radius_meters,
      max_results: 500,
    });

    if (error) throw error;
    if (!data) return [];

    return data.map((row) => ({
      id: row.id,
      dedup_hash: '',
      title: row.title,
      url: row.url,
      source_domain: null,
      start_datetime: row.start_datetime,
      end_datetime: null,
      start_raw: null,
      venue_name: row.venue_name,
      address: null,
      city: null,
      state: null,
      zip_code: null,
      latitude: row.latitude,
      longitude: row.longitude,
      description: null,
      categories: row.categories,
      image_url: row.image_url,
      ticket_url: null,
      price: row.price,
      extraction_method: null,
      confidence: null,
      extracted_at: null,
      created_at: null,
      updated_at: null,
      distance_m: row.distance_m,
    }));
  } catch (rpcError) {
    console.warn('nearby_events RPC failed, falling back to direct query:', rpcError);

    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('events')
      .select('id,title,url,source_domain,start_datetime,end_datetime,start_raw,venue_name,address,city,state,zip_code,latitude,longitude,description,categories,image_url,ticket_url,price,extraction_method,confidence,extracted_at,created_at,updated_at,dedup_hash')
      .not('latitude', 'is', null)
      .not('longitude', 'is', null)
      .gte('start_datetime', now)
      .order('start_datetime', { ascending: true })
      .limit(500);

    if (error) {
      console.error('fallback query also failed:', error);
      throw new Error('Failed to fetch nearby events');
    }
    if (!data) return [];

    return data.map((row) => ({
      id: row.id,
      dedup_hash: row.dedup_hash ?? '',
      title: row.title,
      url: row.url,
      source_domain: row.source_domain,
      start_datetime: row.start_datetime,
      end_datetime: row.end_datetime,
      start_raw: row.start_raw,
      venue_name: row.venue_name,
      address: row.address,
      city: row.city,
      state: row.state,
      zip_code: row.zip_code,
      latitude: row.latitude as number,
      longitude: row.longitude as number,
      description: row.description,
      categories: row.categories,
      image_url: row.image_url,
      ticket_url: row.ticket_url,
      price: row.price,
      extraction_method: row.extraction_method,
      confidence: row.confidence,
      extracted_at: row.extracted_at,
      created_at: row.created_at,
      updated_at: row.updated_at,
      distance_m: null,
    }));
  }
}

export async function searchEvents(query: string): Promise<Event[]> {
  if (!query?.trim()) return [];

  const raw = query.trim();
  if (raw.length > 200) throw new Error('Search query too long (max 200 characters)');

  // The `.or()` argument is PostgREST filter syntax, not a bound parameter, so any
  // character that is meaningful in that grammar has to be removed before the term
  // is interpolated: `,` separates clauses, `.` separates operator parts, and
  // `()`,`:`,`"`,`\` delimit values. `%` and `_` are ILIKE wildcards.
  const q = raw.replace(/[,.()"\\:*%_]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!q) return [];

  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .or(`title.ilike.%${q}%,description.ilike.%${q}%,city.ilike.%${q}%,venue_name.ilike.%${q}%`)
    .not('latitude', 'is', null)
    .not('longitude', 'is', null)
    .gte('start_datetime', now)
    .limit(50);

  if (error) {
    console.error('event search failed:', error);
    throw new Error('Failed to search events');
  }

  return (data || []).map((row) => ({
    id: row.id,
    dedup_hash: row.dedup_hash,
    title: row.title,
    url: row.url,
    source_domain: row.source_domain,
    start_datetime: row.start_datetime,
    end_datetime: row.end_datetime,
    start_raw: row.start_raw,
    venue_name: row.venue_name,
    address: row.address,
    city: row.city,
    state: row.state,
    zip_code: row.zip_code,
    latitude: row.latitude as number,
    longitude: row.longitude as number,
    description: row.description,
    categories: row.categories,
    image_url: row.image_url,
    ticket_url: row.ticket_url,
    price: row.price,
    extraction_method: row.extraction_method,
    confidence: row.confidence,
    extracted_at: row.extracted_at,
    created_at: row.created_at,
    updated_at: row.updated_at,
  }));
}
